import { useState, useEffect, useReducer } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Container,
  styled,
  Alert,
  CircularProgress,
} from "@mui/material";
import CreateOrder from "../../components/m2o-components/create-order";
import CreateASN from "../../components/m2o-components/create-asn";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import { imageService } from "../../utils/services/imageService";
import { makeToOrderService } from "../../utils/services/m2oService";
import { toast } from "react-toastify";
import { clientConfigService } from "../../utils/services/clientConfigService";
import { orderService } from "../../utils/services/orderService";
import buildQuery from "../../utils/query-builder";
import { padNumberWithZeros } from "../../utils/utility";
import { asnService } from "../../utils/services/asnService";

const CustomStepIconRoot = styled("div")(({ theme, ownerState }) => ({
  backgroundColor: ownerState.completed
    ? "#00BF3C"
    : ownerState.active
      ? "#FFD174"
      : "#ccc",
  zIndex: 1,
  color: "#000",
  width: 25,
  height: 25,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
}));

const ColorStepConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 12,
    left: "calc(-50% + 12px)",
    right: "calc(50% + 12px)",
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: "linear-gradient(to right, #00BF3C 50%, #FFD174 50%)",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: "#00BF3C",
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: "#eaeaf0",
    borderRadius: 1,
  },
}));

const init_data = {
  order_id: "",
  order_date: new Date().toISOString().split("T")[0],
  client_id: null,
  subtotal_price: "0",
  fulfillment_status: "pending",
  customer: {
    first_name: "",
    last_name: "",
    company: "",
    // address1: "",
    // address2: "",
    // city: "",
    // province: "",
    // country: "",
    // country_code: "",
    // zip: "",
    phone: "",
    email: "",
    address: {
      address1: "",
      address2: "",
      city: "",
      province: "",
      country: "",
      country_code: "",
      zip: "",
      province_code: "",
    },
  },
  shipping_address: {
    first_name: "",
    last_name: "",
    company: "",
    address1: "",
    address2: "",
    city: "",
    province: "",
    province_code: "",
    country: "",
    country_code: "",
    zip: "",
    phone: "",
    email: "",
  },
  sku_list: [
    {
      product: null,
      sku: "",
      variant_id: "",
      quantity_requested: "",
      quantity_accepted: "",
      instructions: "",
      attachment: null,
      attachmentUrl: null,
      no_of_overpacks: "",
      overpack_type: "",
      items_per_overpack: "",
    },
  ],
  asn_no: "",
  supplier: null,
  purchase_order: null,
  warehouse: null,
  expected_dispatch_date: "",
  shipment_date: "",
  bol_no: "",
  status: "Created",
  additional_info: {
    driver_name: "",
    vehicle_license: "",
  },
};

function CustomStepIcon(props) {
  const { active, completed, className, icon } = props;

  return (
    <CustomStepIconRoot
      ownerState={{ active, completed }}
      className={className}
    >
      {icon}
    </CustomStepIconRoot>
  );
}

//
const reducer = (state, action) => {
  switch (action.type) {
    case "UPDATE_FIELD":
      return {
        ...state,
        ...action.payload,
      };
    case "UPDATE_SKU_LIST":
      return {
        ...state,
        sku_list: action.payload,
      };
    case "UPDATE_SHIPPING_ADDRESS":
      return {
        ...state,
        shipping_address: {
          ...state.shipping_address,
          ...action.payload,
        },
      };
    case "UPDATE_CUSTOMER":
      return {
        ...state,
        customer: {
          ...state.customer,
          ...action.payload,
        },
      };
    case "RESET":
      return init_data;
    default:
      return state;
  }
};
// Main Stepper Component
const AddM2O = () => {
  const { id } = useParams();
  const [activeStep, setActiveStep] = useState(id === "create" ? 0 : 1);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const clientId = localStorage.getItem("selected_order_client_id");
  const orderType = localStorage.getItem("orderType") || "B2C";
  const rl_oms_token = localStorage.getItem("rl_oms_token");
  const [sameAsShippingAddress, setSameAsShippingAddress] = useState(false);

  const [m2oData, dispatchM2OData] = useReducer(reducer, init_data);

  const steps = ["Create Order", "Create P.O."];
  const navigate = useNavigate();

  // Save to localStorage with expiration
  const saveToLocalStorage = (data) => {
    const expirationTime = new Date().getTime() + 60 * 60 * 1000; // 1 hour from now
    const storageData = {
      data,
      expires: expirationTime,
    };
    if (sameAsShippingAddress) {
      storageData.data.customer = {
        ...storageData.data.shipping_address,
      };
    }
    localStorage.setItem("m2oOrderData", JSON.stringify(storageData));
  };

  // Load from localStorage
  const loadFromLocalStorage = () => {
    const storedData = localStorage.getItem("m2oOrderData");
    if (storedData) {
      const { data, expires } = JSON.parse(storedData);
      if (new Date().getTime() < expires) {
        return data;
      }
    }
    return null;
  };

  // Clear localStorage
  const clearLocalStorage = () => {
    localStorage.removeItem("m2oOrderData");
    dispatchM2OData({ type: "RESET" });
    setErrors({});
    setSaveError("");
    setSaveSuccess("");
    setActiveStep(0);
    setSameAsShippingAddress(false);
    localStorage.removeItem("selected_order_client_id");
  };

  // Load saved data on component mount
  useEffect(() => {
    const savedData = loadFromLocalStorage();
    if (savedData && id !== "create") {
      dispatchM2OData({ type: "UPDATE_FIELD", payload: savedData });
    }
  }, [id]);

  useEffect(() => {
    const checkExpiration = () => {
      const storedData = localStorage.getItem("m2oOrderData");
      if (storedData) {
        const { expires } = JSON.parse(storedData);
        if (new Date().getTime() > expires) {
          clearLocalStorage();
        }
      }
    };

    checkExpiration();
    const interval = setInterval(checkExpiration, 60000); // Check every minute
    return () => {
      clearInterval(interval);
    };
  }, []);

  const validateOrder = () => {
    const newErrors = {};

    // Order validation
    if (!m2oData.order_id?.trim()) {
      newErrors.order_id = "Order ID is required";
    }

    const { shipping_address } = m2oData;
    if (!shipping_address.first_name?.trim()) {
      newErrors.first_name = "First Name is required";
    }
    if (!shipping_address.last_name?.trim()) {
      newErrors.last_name = "Last Name is required";
    }
    if (!shipping_address.address1?.trim()) {
      newErrors["shipping_address_address1"] = "Address is required";
    }
    if (!shipping_address.city?.trim()) {
      newErrors["shipping_address_city"] = "City is required";
    }
    if (!shipping_address.zip?.trim()) {
      newErrors["shipping_address_zip"] = "ZIP code is required";
    }
    if (!shipping_address.province?.trim()) {
      newErrors["shipping_address_province"] = "Province is required";
    }
    if (!shipping_address.province_code?.trim()) {
      newErrors["shipping_address_province_code"] = "Province code is required";
    }

    if (!shipping_address.country?.trim()) {
      newErrors["shipping_address_country"] = "Country is required";
    }
    if (!shipping_address.country_code?.trim()) {
      newErrors["shipping_address_country_code"] = "Country Code is required";
    }
    if (!shipping_address.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(shipping_address.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!sameAsShippingAddress) {
      const { customer } = m2oData;
      if (!customer.first_name?.trim()) {
        newErrors.first_name = "First Name is required";
      }
      if (!customer.last_name?.trim()) {
        newErrors.last_name = "Last Name is required";
      }
      if (!customer.address1?.trim()) {
        newErrors.address1 = "Address is required";
      }
      if (!customer.city?.trim()) {
        newErrors["customer_city"] = "City is required";
      }
      if (!customer.zip?.trim()) {
        newErrors["customer_zip"] = "ZIP code is required";
      }
      if (!customer.province?.trim()) {
        newErrors["customer_province"] = "Province is required";
      }
      if (!customer.province_code?.trim()) {
        newErrors["customer_province_code"] = "Province Code is required";
      }

      if (!customer.country?.trim()) {
        newErrors["customer_country"] = "Country is required";
      }
      if (!customer.country_code?.trim()) {
        newErrors["customer_country_code"] = "Country Code is required";
      }
      if (!customer.email?.trim()) {
        newErrors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(customer.email)) {
        newErrors.email = "Invalid email format";
      }
    }

    // SKU list validation
    const productErrors = m2oData.sku_list.map((sku, index) => {
      const skuErrors = {};
      if (!sku.product) {
        skuErrors.product = "Product is required";
      }
      if (!sku?.sku) {
        skuErrors.sku = "SKU is required";
      }
      if (!sku.quantity_requested || parseInt(sku.quantity_requested) <= 0) {
        skuErrors.quantity_requested = "Quantity must be greater than 0";
      }
      return skuErrors;
    });
    const clientIdToUse = clientId;
    if (!clientIdToUse) {
      newErrors.client = "Client is required";
    }

    setErrors({ ...newErrors, sku_list: productErrors });
    return (
      Object.keys(newErrors).length === 0 &&
      productErrors.every((err) => Object.keys(err).length === 0)
    );
  };

  const validateASN = () => {
    const newErrors = {};

    if (!m2oData.asn_no?.trim()) {
      newErrors.asn_no = "ASN Number is required";
    }
    if (!m2oData.supplier) {
      newErrors.supplier = "Supplier is required";
    }
    if (!m2oData.expected_dispatch_date) {
      newErrors.expected_dispatch_date = "Expected Dispatch Date is required";
    }
    if (!m2oData.purchase_order) {
      newErrors.purchase_order = "Master Purchase Order is required";
    }

    const clientIdToUse = clientId;
    if (!clientIdToUse) {
      newErrors.client = "Client is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (activeStep === 0) {
      const isValid = validateOrder();
      if (!isValid) return;

      const updatedData = { ...m2oData };
      saveToLocalStorage(updatedData);
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    if (activeStep === 1) {
      saveToLocalStorage(m2oData);
    }
  };

  const handlePlaceOrder = () => {
    const isValid = validateOrder();
    if (isValid) {
      saveToLocalStorage(m2oData);
      handleNext();
    }
  };

  const handleSaveM2O = async () => {
    const isValid = validateASN();
    if (!isValid) return;

    setIsLoading(true);
    setSaveError("");
    setSaveSuccess("");

    try {
      const asnData = {
        asn_no: m2oData.asn_no,
        supplier: m2oData.supplier?._id || m2oData.supplier,
        purchase_order: m2oData.purchase_order?._id || m2oData.purchase_order,
        warehouse: m2oData.warehouse?._id || m2oData.warehouse,
        client: clientId,
        expected_dispatch_date: m2oData.expected_dispatch_date,
        shipment_date: m2oData.shipment_date || null,
        bol_no: m2oData.bol_no || "",
        additional_info: {
          driver_name: m2oData.additional_info?.driver_name || "",
          vehicle_license: m2oData.additional_info?.vehicle_license || "",
        },
        sku_list: m2oData.sku_list.map((item) => ({
          product: item.product?._id,
          sku: item.sku.sku,
          variant_id: item.product?.variants?.find((v) => v.sku === item.sku)
            ?._id,
          quantity_requested: parseInt(item.quantity_requested) || 0,
        })),
        order_placed: false,
        type: "M2O",
        order: null,
      };
      const orderData = {
        ...asnData,
        order_id: m2oData.order_id,
        order_date: m2oData.order_date,
        client_id: asnData.client,
        subtotal_price: calculateSubtotal(),
        fulfillment_status: "processing",
        shipping_address: {
          ...m2oData.shipping_address,
          name: `${m2oData.shipping_address.first_name} ${m2oData.shipping_address.last_name}`,
        },
        customer: {
          first_name: m2oData.customer.first_name,
          last_name: m2oData.customer.last_name,
          company: m2oData.customer.company,
          phone: m2oData.customer.phone,
          email: m2oData.customer.email,
          address: {
            address1: m2oData.customer.address1,
            address2: m2oData.customer.address2,
            city: m2oData.customer.city,
            province: m2oData.customer.province,
            province_code: m2oData.customer.province_code,
            country: m2oData.customer.country,
            country_code: m2oData.customer.country_code,
            zip: m2oData.customer.zip,
          },
        },

        sku_list: m2oData.sku_list.map((item) => {
          let variant = item.product?.variants?.find(
            (v) => v.sku === item.sku.sku,
          );
          return {
            ...item,
            sku: item.sku.sku,
            variant_id: variant?._id,
            product: item.product?._id || item.product,
            name: item?.product?.name || "",
            price: variant?.price || "",
            title: item?.product?.name || "",
          };
        }),
        type: "M2O",
        sub_type: orderType,
      };

      const orderResponse = await makeToOrderService.create({
        ...orderData,
        rl_oms_token: rl_oms_token,
      });

      console.log(orderResponse);
      clearLocalStorage();
      navigate("/order-management?child=true");
      toast.success("M2O Order and P.O. created successfully!", {
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Save failed:", error);
      setSaveError(
        error?.response?.data?.message ||
          "Failed to save M2O order. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const calculateSubtotal = () => {
    return m2oData.sku_list
      .reduce((sum, sku) => {
        return (
          sum +
          parseFloat(sku?.price || 0) * parseInt(sku?.quantity_requested || 0)
        );
      }, 0)
      .toString();
  };

  const handleCancel = () => {
    setActiveStep(0);
    dispatchM2OData({ type: "RESET" });
    setErrors({});
    setSaveError("");
    setSaveSuccess("");
    clearLocalStorage();
  };

  const handleAttachmentChange = async (e, skuIndex) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const file = files[0];
      try {
        const attachmentData = await imageService.upload(file);
        const attachmentUrl = URL.createObjectURL(file);

        const updatedSkuList = [...m2oData.sku_list];
        updatedSkuList[skuIndex] = {
          ...updatedSkuList[skuIndex],
          attachment: {
            key: attachmentData.key,
            fileName: file.name,
          },
          attachmentUrl,
        };
        dispatchM2OData({
          type: "UPDATE_FIELD",
          payload: { sku_list: updatedSkuList },
        });
      } catch (error) {
        console.error("Attachment upload failed:", error);
        setSaveError("Failed to upload attachment. Please try again.");
      } finally {
        e.target.value = null;
      }
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchOrderPrefix();
      fetchPurchaseOrderPrefix();
    }
  }, [clientId]);
  let purchase_order_prefix = "";

  const fetchPurchaseOrderPrefix = async () => {
    try {
      const response = await clientConfigService.fetch(
        clientId,
        1,
        0,
        "purchase_order_prefix",
      );
      fetchPurchaseOrderCount();

      if (response && response.total > 0) {
        purchase_order_prefix = response.data[0].value || "";
      }
    } catch (error) {
      console.error("Error fetching order prefix:", error);
    }
  };

  const fetchPurchaseOrderCount = async () => {
    try {
      const response = await asnService.fetch("$limit=0", clientId);
      dispatchM2OData({
        type: "UPDATE_FIELD",
        payload: {
          asn_no: `${purchase_order_prefix}${padNumberWithZeros(
            response.total + 1,
          )}`,
        },
      });
    } catch (error) {
      console.error("Error fetching order count:", error);
    }
  };

  let orderPrefix = "";
  const fetchOrderPrefix = async () => {
    try {
      const response = await clientConfigService.fetch(
        clientId,
        1,
        0,
        "order_id_prefix",
      );
      fetchOrderCount();

      if (response && response.total > 0) {
        orderPrefix = response.data[0].value || "".trim();
      }
    } catch (error) {
      console.error("Error fetching order prefix:", error);
    }
  };

  const fetchOrderCount = async () => {
    try {
      const response = await orderService.fetchOrderCount(clientId);
      dispatchM2OData({
        type: "UPDATE_FIELD",
        payload: {
          order_id: `${orderPrefix}${padNumberWithZeros(response.total + 1)}`,
        },
      });
    } catch (error) {
      console.error("Error fetching order count:", error);
    }
  };

  return (
    <Container maxWidth={false} sx={{ py: 4, width: "100%", paddingTop: 1 }}>
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        connector={<ColorStepConnector />}
        sx={{
          mb: 1,
          width: "60%",
          mx: "auto",
          "& .MuiStepLabel-label": {
            mt: 1,
            fontSize: 10,
          },
          "& .MuiStepLabel-root": {
            flexDirection: "column",
          },
        }}
      >
        {steps.map((label, index) => (
          <Step key={label} completed={index < activeStep}>
            <StepLabel
              style={{ fontSize: 10 }}
              StepIconComponent={CustomStepIcon}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box
        sx={{
          p: 4,
          maxHeight: "75vh",
          overflowY: "auto",
          width: "100%",
          border: "1px solid",
          borderColor: "grey.200",
          borderRadius: 1,
          backgroundColor: "var(--bg-paper)",
        }}
      >
        {/* Alert Messages */}
        {saveError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setSaveError("")}
          >
            {saveError}
          </Alert>
        )}
        {saveSuccess && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSaveSuccess("")}
          >
            {saveSuccess}
          </Alert>
        )}

        {activeStep === 0 && (
          <CreateOrder
            clientId={clientId}
            setErrors={setErrors}
            errors={errors}
            m2oData={m2oData}
            onM2ODataChange={(data) =>
              dispatchM2OData({ type: "UPDATE_FIELD", payload: data })
            }
            onShippingAddressChange={(data) => {
              console.log("Shipping Address Data:", data);

              dispatchM2OData({
                type: "UPDATE_SHIPPING_ADDRESS",
                payload: data,
              });
            }}
            onCustomerAddressChange={(data) =>
              dispatchM2OData({ type: "UPDATE_CUSTOMER", payload: data })
            }
            handleAttachmentChange={handleAttachmentChange}
            sameAsShippingAddress={sameAsShippingAddress}
            setSameAsShippingAddress={setSameAsShippingAddress}
          />
        )}
        {activeStep === 1 && (
          <CreateASN
            clientId={clientId}
            params={id}
            m2oData={m2oData}
            onM2ODataChange={(data) =>
              dispatchM2OData({ type: "UPDATE_FIELD", payload: data })
            }
            errors={errors}
            setErrors={setErrors}
          />
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button
            onClick={handleCancel}
            variant="outlined"
            color="secondary"
            size="small"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {activeStep === 1 && id === "create" && (
              <Button
                onClick={handleBack}
                variant="outlined"
                color="primary"
                size="small"
                disabled={isLoading}
              >
                Back to Order
              </Button>
            )}
            {activeStep === 0 ? (
              <Button
                onClick={handlePlaceOrder}
                variant="contained"
                color="error"
                size="small"
                disabled={isLoading}
              >
                Save & Next
              </Button>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {isLoading && <CircularProgress size={20} />}
                <Button
                  onClick={handleSaveM2O}
                  variant="contained"
                  color="success"
                  size="small"
                  disabled={isLoading}
                >
                  {isLoading ? "Creating..." : "Create M2O Order"}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default AddM2O;
